import { useCallback, useEffect, useState } from 'react'
import type { UUID } from '@/index'
import type { AgentActivityEventPayload } from '@/infra/sse/activity/payloads'
import type { FormattedAgentActivity, FormattedUserActivity } from '../utils'
import { formatAgentActivityEvent } from '../utils'

interface UseLatestActivityOptions {
  readonly sessionId: UUID
  readonly initialActivity?: FormattedAgentActivity | FormattedUserActivity | null | undefined
}

interface UseLatestActivityReturn {
  readonly activity: FormattedAgentActivity | FormattedUserActivity | null
  readonly isActive: boolean
}

const ACTIVITY_FRESHNESS_MS = 2000

export function useLatestActivity(options: UseLatestActivityOptions): UseLatestActivityReturn {
  const { sessionId, initialActivity } = options
  const [activity, setActivity] = useState<FormattedAgentActivity | FormattedUserActivity | null>(
    initialActivity ?? null,
  )
  const [lastEventTime, setLastEventTime] = useState<number>(0)
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    setActivity(initialActivity ?? null)
  }, [initialActivity])

  const handleActivityEvent = useCallback(
    (event: CustomEvent<AgentActivityEventPayload>) => {
      const payload = event.detail

      if (payload.eventMetadata.claudeSessionId !== sessionId) {
        return
      }

      const formatted = formatAgentActivityEvent(payload)
      if (formatted) {
        setActivity(formatted)
        setLastEventTime(Date.now())
        setIsActive(true)
      }
    },
    [sessionId],
  )

  useEffect(() => {
    const handler = handleActivityEvent as EventListener
    globalThis.addEventListener('activity-event', handler)
    return () => {
      globalThis.removeEventListener('activity-event', handler)
    }
  }, [handleActivityEvent])

  useEffect(() => {
    if (!isActive || lastEventTime === 0) return

    const timeoutId = setTimeout(() => {
      setIsActive(false)
    }, ACTIVITY_FRESHNESS_MS)

    return () => clearTimeout(timeoutId)
  }, [isActive, lastEventTime])

  return {
    activity,
    isActive,
  }
}
